/*
 * Header floating island enhancements, styled in theme.css under "HEADER - FLOATING ISLAND".
 *
 * - Scroll progress: exposes --header-scroll-progress (0 to 1) on the header for the progress line.
 * - Nav indicator: a single pill that slides between hovered or focused top-level links on pointer devices.
 * - Cart bump: animates the cart icon whenever the theme reports a cart change.
 *
 * Everything is additive. If this file fails to load, the header keeps working and falls back to CSS hover states.
 */

const header = document.querySelector('x-header.header');

if (header) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Scroll progress
  const progress = document.createElement('span');
  progress.className = 'header__progress';
  progress.setAttribute('aria-hidden', 'true');
  header.append(progress);

  let progressFrame = 0;

  const updateProgress = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const ratio = scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0;
    header.style.setProperty('--header-scroll-progress', ratio.toFixed(4));
    progressFrame = 0;
  };

  const requestProgressUpdate = () => {
    if (!progressFrame) {
      progressFrame = requestAnimationFrame(updateProgress);
    }
  };

  window.addEventListener('scroll', requestProgressUpdate, { passive: true });
  window.addEventListener('resize', requestProgressUpdate, { passive: true });
  updateProgress();

  // Sliding nav indicator
  const nav = header.querySelector('.header__primary-nav');
  const finePointer = window.matchMedia('(pointer: fine) and (min-width: 1000px)');

  if (nav && finePointer.matches) {
    const links = [...nav.querySelectorAll('a.link-reversed')].filter((link) => {
      return !link.closest('.header__dropdown-menu, header-mega-menu');
    });

    if (links.length > 0) {
      const indicator = document.createElement('span');
      indicator.className = 'header__nav-indicator';
      indicator.setAttribute('aria-hidden', 'true');
      nav.prepend(indicator);

      const moveTo = (link) => {
        // The pill is positioned against its offset parent (the header), not the nav itself.
        const navRect = (indicator.offsetParent || nav).getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();
        const wasHidden = !indicator.classList.contains('is-visible');

        // Appear in place instead of sliding in from the corner when the pill was hidden.
        if (wasHidden || reducedMotion.matches) {
          indicator.style.transition = 'none';
        }

        indicator.style.width = `${linkRect.width}px`;
        indicator.style.height = `${linkRect.height}px`;
        indicator.style.transform = `translate(${linkRect.left - navRect.left}px, ${linkRect.top - navRect.top}px)`;

        if (wasHidden || reducedMotion.matches) {
          indicator.getBoundingClientRect();
          indicator.style.transition = '';
        }

        indicator.classList.add('is-visible');
      };

      const hide = () => indicator.classList.remove('is-visible');

      links.forEach((link) => {
        link.addEventListener('pointerenter', () => moveTo(link));
        link.addEventListener('focus', () => moveTo(link));
      });

      nav.addEventListener('pointerleave', hide);
      nav.addEventListener('focusout', (event) => {
        if (!nav.contains(event.relatedTarget)) {
          hide();
        }
      });
    }
  }

  // Collection mega menu bubble: start the reveal under the center of the hovered link.
  if (nav) {
    nav.querySelectorAll('a[aria-controls]').forEach((trigger) => {
      const menu = document.getElementById(trigger.getAttribute('aria-controls'));

      if (!menu || !menu.classList.contains('mega-menu--collection')) {
        return;
      }

      const setBubbleOrigin = () => {
        const headerRect = header.getBoundingClientRect();
        const triggerRect = trigger.getBoundingClientRect();
        menu.style.setProperty('--mega-bubble-x', `${Math.round(triggerRect.left + triggerRect.width / 2 - headerRect.left)}px`);
      };

      trigger.addEventListener('pointerenter', setBubbleOrigin);
      trigger.addEventListener('focus', setBubbleOrigin);
    });
  }

  // One-line menu. When the links do not fit beside the centered logo (narrow laptops, long menu titles, or the
  // extra padding of the floating island), collapse them into the theme's Menu button instead of wrapping.
  const desktopQuery = window.matchMedia('(min-width: 1000px)');
  const menuList = nav ? nav.querySelector('ul.contents') : null;

  if (nav && menuList) {
    const updateCondensed = () => {
      if (!desktopQuery.matches) {
        header.classList.remove('is-nav-condensed');
        return;
      }

      // Measure with the full list shown, then decide within the same frame so nothing flashes.
      header.classList.remove('is-nav-condensed');
      header.classList.toggle('is-nav-condensed', nav.scrollWidth > nav.clientWidth + 1);
    };

    new ResizeObserver(updateCondensed).observe(nav);
    desktopQuery.addEventListener('change', updateCondensed);

    if (document.fonts) {
      document.fonts.ready.then(updateCondensed);
    }

    updateCondensed();
  }

  // Cart bump
  const cartLink = header.querySelector('.header__cart-link');

  if (cartLink) {
    document.documentElement.addEventListener('cart:change', () => {
      if (reducedMotion.matches) {
        return;
      }

      cartLink.classList.remove('is-bumping');
      cartLink.getBoundingClientRect();
      cartLink.classList.add('is-bumping');
    });

    cartLink.addEventListener('animationend', () => cartLink.classList.remove('is-bumping'));
  }
}
